import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@shared/ui';
import { useShipmentQuery } from '@entities/logistics';
import { AssignCarrierDialog } from './assign-carrier-dialog';

interface ShipmentSectionProps {
  orderId: string;
  canAssign: boolean;
}

export function ShipmentSection({ orderId, canAssign }: ShipmentSectionProps) {
  const { t } = useTranslation();
  const { data: shipment, isLoading, isError, error } = useShipmentQuery(orderId);
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('shipment.section.title')}</CardTitle>
        {shipment && canAssign ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {shipment.carrier ? t('shipment.section.update') : t('shipment.section.assign')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <p className="text-sm opacity-70">
            {t('shipment.section.notFound')}
            {error ? `: ${error.message}` : ''}
          </p>
        ) : shipment ? (
          <div className="flex flex-col gap-3 text-sm">
            <div className="grid gap-2 md:grid-cols-2">
              <Field label={t('shipment.section.fields.carrier')} value={shipment.carrier?.name ?? '—'} />
              <Field label={t('shipment.section.fields.tracking')} value={shipment.trackingNumber ?? '—'} />
              <Field label={t('shipment.section.fields.eta')} value={shipment.etaDate ?? '—'} />
              <div>
                <p className="text-xs opacity-70">{t('shipment.section.fields.status')}</p>
                <Badge tone="info">{shipment.status}</Badge>
              </div>
            </div>
            {shipment.history.length > 0 ? (
              <details className="rounded-md border bg-background">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                  {t('shipment.section.fields.history')} ({shipment.history.length})
                </summary>
                <ol className="space-y-2 px-3 py-2 text-sm">
                  {shipment.history.map((h, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-xs opacity-70">{new Date(h.at).toLocaleString()}</span>
                      <span className="font-medium">{h.status}</span>
                      {h.note ? <span className="opacity-70">— {h.note}</span> : null}
                    </li>
                  ))}
                </ol>
              </details>
            ) : null}

            {open ? (
              <AssignCarrierDialog
                shipment={shipment}
                orderId={orderId}
                open={open}
                onOpenChange={setOpen}
              />
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs opacity-70">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
